import React, { useImperativeHandle, useRef, useState, type PropsWithChildren } from "react";
import type { Table } from "@tanstack/react-table";
import { X } from "lucide-react";
import { useDebounce } from "react-use";

import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Kbd } from "@/components/ui/kbd";

import { DataTableExportButton } from "../data-table-export-button";
import { DataTableRefreshButton } from "../data-table-refresh-button";
import { DataTableBulkActionsToolbar } from "./data-table-bulk-actions-toolbar";
import { DataTableFilterPopover } from "./data-table-filter-popover";
import { DataTableSelectedFilters } from "./data-table-selected-filters";
import { DataTableViewOptions } from "./data-table-view-options";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  isLoading?: boolean;
  onRefresh?: () => void;
  onExport?: () => void;
  isExporting?: boolean;
  bulkActions?: React.ReactNode;
  children?: React.ReactNode;
  rightActions?: React.ReactNode;
  ref?: React.Ref<DataTableToolbarRef>;
  hideViewOptions?: boolean;
}

export interface DataTableToolbarRef {
  focusSearch: () => void;
  clearSearch: () => void;
  refresh: () => void;
  resetFilters: () => void;
}

export function DataTableToolbar<TData>({
  children,
  table,
  isLoading,
  onRefresh,
  onExport,
  isExporting,
  bulkActions,
  rightActions,
  hideViewOptions,
  ref,
}: PropsWithChildren<DataTableToolbarProps<TData>>) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tableState = table.getState();
  const [searchValue, setSearchValue] = useState<string>((tableState.globalFilter as string | undefined) ?? "");

  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      searchInputRef.current?.focus();
    },
    clearSearch: () => {
      setSearchValue("");
      table.resetGlobalFilter();
      searchInputRef.current?.blur();
    },
    refresh: () => {
      onRefresh?.();
    },
    resetFilters: () => {
      setSearchValue("");
      table.resetGlobalFilter();
      table.resetColumnFilters();
    },
  }));

  useDebounce(
    () => {
      table.setGlobalFilter(searchValue);
    },
    300,
    [searchValue],
  );

  const isFiltered = tableState.columnFilters.length > 0 || !!tableState.globalFilter;
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const hasSelectedRows = selectedRows.length > 0;
  const showBulkActions = hasSelectedRows && bulkActions;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <DataTableFilterPopover table={table} />
        {table.options.enableGlobalFilter && (
          <InputGroup className="h-9 w-full bg-white md:w-80" data-table-search="true">
            <InputGroupInput
              ref={searchInputRef}
              className="h-9 text-sm"
              placeholder="Search"
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
            />
            <InputGroupAddon align="inline-end">
              <Kbd>/</Kbd>
            </InputGroupAddon>
          </InputGroup>
        )}

        {children}

        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchValue("");
              table.resetGlobalFilter();
              table.resetColumnFilters();
            }}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <X />
          </Button>
        )}

        <span className="flex-1"></span>

        {showBulkActions ? (
          <DataTableBulkActionsToolbar selectedCount={selectedRows.length}>{bulkActions}</DataTableBulkActionsToolbar>
        ) : (
          <>
            {onExport && <DataTableExportButton onExport={onExport} isLoading={isExporting} className="size-9" />}
            <DataTableRefreshButton onRefresh={onRefresh} isRefreshing={isLoading} className="size-9" />
            {!hideViewOptions && <DataTableViewOptions table={table} />}
            {rightActions}
          </>
        )}
      </div>

      <DataTableSelectedFilters table={table} />
    </div>
  );
}
